// Print just a data table, in its own popup window.
//
// `window.print()` prints the whole page - sidebar, filter bar, buttons and all -
// which is not what anyone wants from a report. These helpers build a standalone
// document containing only the table and print that instead.

export interface PrintTableOptions {
  title: string;
  subtitle?: string;
  head: string[];
  // each row is a list of already-formatted cells; a cell may be raw HTML (an <img>)
  rows: (string | number)[][];
  // optional bold summary row at the bottom
  footer?: { label: string; colSpan: number; cells: (string | number)[] };
}

const PRINT_STYLES = `
  body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#222}
  h2{margin:0 0 4px} .sub{color:#666;margin-bottom:16px}
  table{border-collapse:collapse;width:100%}
  th,td{border:1px solid #bbb;padding:6px 8px;text-align:center;font-size:13px}
  th{background:#f17400;color:#fff}
  tfoot td{font-weight:bold;background:#f5f5f5}
`;

export const buildPrintTableHtml = ({ title, subtitle, head, rows, footer }: PrintTableOptions): string => {
  const body = rows
    .map((cells) => `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`)
    .join("");
  const foot = footer
    ? `<tfoot><tr><td colspan="${footer.colSpan}">${footer.label}</td>${footer.cells
        .map((c) => `<td>${c}</td>`)
        .join("")}</tr></tfoot>`
    : "";
  return `<!doctype html><html><head><title>${title}</title>
    <style>${PRINT_STYLES}</style></head><body>
    <h2>${title}</h2>
    ${subtitle ? `<div class="sub">${subtitle}</div>` : ""}
    <table><thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>${body}</tbody>
    ${foot}
    </table></body></html>`;
};

// Returns false when the popup was blocked, so the caller can show its own message.
export const printHtmlTable = (options: PrintTableOptions): boolean => {
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) return false;
  w.document.write(buildPrintTableHtml(options));
  w.document.close();
  w.focus();
  w.print();
  return true;
};
